import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Autocomplete, Box, Button, Checkbox, FormControl, IconButton, InputLabel, ListSubheader, ListSubheaderProps, MenuItem, Select, TextField } from '@mui/material'
import { useAppSelector } from '../../../redux/store'
import { ChangeEvent, useState } from 'react'
import debounce from '../../../helpers/debounce'
import { Groups } from '../../../types'
import { selectGroups } from '../../../redux/customer'
import { CheckBox, CheckBoxOutlineBlank, RestartAlt } from '@mui/icons-material'
import { ComplianceFilterOption } from './types'
import { Link } from 'react-router-dom'

const icon = <CheckBoxOutlineBlank fontSize="small" />
const checkedIcon = <CheckBox fontSize="small" />

const statuses = [
  { name: 'Inactive', value: 4 },
  { name: 'Attention Needed', value: 2 },
  { name: 'Action Required', value: 3 },
  { name: 'Up to Date', value: 1 },
]

const complianceTrees = [
  { name: 'Overall', field: 'status' },
  { name: 'OSHA/HIPAA', field: 'osha_hipaa_compliance_status' },
  { name: 'Training', field: 'training_compliance_status' },
  { name: 'Medications', field: 'kit_compliance_status' },
  { name: 'Devices', field: 'device_compliance_status' },
  { name: 'Biological', field: 'bio_compliance_status' },
  { name: 'Waterline Management', field: 'waterline_compliance_status' },
  { name: 'Medical Waste', field: 'mwaste_compliance_status' },
  { name: 'Amalgam Waste', field: 'amalgam_compliance_status' },
]

const complianceFilterOptions: ComplianceFilterOption[] = complianceTrees.flatMap(tree => [
  {
    name: tree.name,
    group: tree.name,
    field: '',
    value: 0,
  },
  ...statuses.map(status => ({
    name: `${tree.name} - ${status.name}`,
    group: tree.name,
    field: tree.field,
    value: status.value,
  })),
])

interface FiltersProps {
  onChangeFilter: (field: string, value: ComplianceFilterOption[] | Groups[] | string) => void
  onClearFilters: () => void
}

const Filters = ({ onChangeFilter, onClearFilters }: FiltersProps) => {
  const groups = useAppSelector(selectGroups)

  const [search, setSearch] = useState('')
  const [selectedGroups, setSelectedGroups] = useState([] as Groups[])
  const [selectedCompliance, setSelectedCompliance] = useState([] as ComplianceFilterOption[])

  const handleSearch = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const searchValue = event.target.value
    setSearch(searchValue)
    onChangeFilter('search', searchValue)
  }

  const debouncedHandleSearch = debounce(handleSearch, 50)

  const handleChangeSelect = (field: string, value: ComplianceFilterOption[] | Groups[]) => {
    if (field === 'compliance') {
      setSelectedCompliance(value as ComplianceFilterOption[])
    } else {
      setSelectedGroups(value as Groups[])
    }
    onChangeFilter(field, value)
  }

  const clearFilters = () => {
    setSelectedCompliance([])
    setSelectedGroups([])
    onClearFilters()
  }

  return (
    <Box className='flex justify-between' sx={{ marginBottom: '13px' }}>
      <Box className='flex'>
        <FormControl fullWidth size="small">
          <Autocomplete
            className='group-filter'
            style={{ minWidth: 200, maxWidth: 500, marginRight: '16px' }}
            multiple
            disableCloseOnSelect
            options={complianceFilterOptions}
            getOptionLabel={(option) => option.name}
            renderOption={(props, option, { selected }) => option.name === option.group ? (
              <ListSubheader key={option.name}>{option.group}</ListSubheader>
            ) : (
              <li {...props}>
                <Checkbox key={option.name} icon={icon} checkedIcon={checkedIcon} style={{ marginRight: 8 }} checked={selected} />
                {option.name}
              </li>
            )}
            size="small"
            value={selectedCompliance}
            onChange={(e, value) => handleChangeSelect('compliance', value)}
            renderInput={(params) => <TextField {...params} label="Filter by Compliance" />}
          />
        </FormControl>
        <FormControl fullWidth size="small">
          {groups.length > 0 ? (
            <Autocomplete
              className='compliance-filter'
              style={{ minWidth: 200, maxWidth: 500, marginRight: '16px' }}
              multiple
              disableCloseOnSelect
              options={groups}
              getOptionLabel={(option) => option.name}
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox key={option.group_id} icon={icon} checkedIcon={checkedIcon} style={{ marginRight: 8 }} checked={selected} />
                  {option.name}
                </li>
              )}
              size="small"
              value={selectedGroups}
              onChange={(e, value) => handleChangeSelect('groups', value)}
              renderInput={(params) => <TextField {...params} label="Filter by Group" />}
            />
          ) : (
            <FormControl fullWidth size="small">
              <InputLabel id='groups'>Filter by Groups</InputLabel>
              <Select
                labelId='groups'
                label='Filter by Groups'
                style={{ minWidth: 200, maxWidth: 500, marginRight: '16px' }}
              >
                <MenuItem>
                  <Link to='/corp/groups'>Click here to manage your groups.</Link>
                </MenuItem>
              </Select>
            </FormControl>
          )}
        </FormControl>
        {(selectedGroups.length > 0 || selectedCompliance.length > 0) && (
          <Button
            onClick={clearFilters}
            sx={{ color: '#0086b8', width: '280px' }}
            startIcon={<RestartAlt sx={{ color: '#0086b8' }} />}>
            Clear Filters
          </Button>
        )}
      </Box>
      <TextField
        sx={{ minWidth: 200 }}
        size="small"
        label="Search"
        onChange={debouncedHandleSearch}
        InputProps={
          {
            endAdornment: (
              <IconButton>
                <FontAwesomeIcon icon="magnifying-glass" size="sm" className="m-l-8" />
              </IconButton>
            )
          }
        }
      />
    </Box>
  )
}

export default Filters